// Narrative copy for the dashboard's right-hand panel.
//
// Source: "hvsourcedata/Detailed_template_v3.csv" (Narrative/Insights column),
// which supersedes v2. v3 carries all three levels — National, State, and
// County — as separate rows for every category.
//
// Copy is looked up in two layers, most specific first:
//
//   1. CATEGORY_NARRATIVES[categoryId]  — the "Metric/Category" dropdown
//   2. NARRATIVES[groupId]              — the "Group" dropdown
//
// and each layer may be a single string or per-scope copy keyed by geographic
// scope. A category entry that omits the current scope falls through to the
// group template, so an override can be as narrow as one cell of the sheet.
//
// CATEGORY_NARRATIVES is empty today, and that is a finding rather than an
// omission: in v3 every category inside a group reads the same paragraph at a
// given level. (County "Number of graduates" and "Annual earnings" differ by a
// stray double space only.) So a group template still describes the whole
// group and pulls several values at once, and the panel's paragraph changes
// with the Group dropdown, not the Category dropdown. The category layer exists
// so that when the client does author copy for a single category, it drops in
// without reshaping the lookup.
//
// The CSV's {the U.S.} / {State} / {County} all map to {place}, which reads
// "the U.S." / "Alabama" / "Chippewa, Michigan" by scope. Once that phrase is
// normalized, Households and Adjacent fields are identical across all three
// levels, so one template covers them. Workforce needs a county variant because
// education, tenure, age, and gender are state-only fields.
//
// Not yet carried over from v3: the National row for Adjacent fields ends with
// "In home visiting, the average annual earnings is XX." The client's own Notes
// column flags XX as unfilled ("Add home visitor average salary to narrative"),
// so the sentence is held back rather than shipped with a literal XX. When the
// figure lands it belongs in a national-scope entry on ADJACENT_FIELDS.
//
// Tokens:
//   {place}            the geography phrase
//   {<MetricId>}       a metric value, formatted by the registry —
//                      {total_HV} 1,234  {female} 97.1%  {AnnualEarn_HC} $48,120
//   {lowestCount} / {highestCount}        derived: lowest / highest of the four
//                                         adjacent-field graduate counts
//   {lowestprogram} / {highestprogram}    the programs those two belong to
//   {lowestEstimate} / {highestEstimate}  derived: lowest / highest of the four
//                                         adjacent-field earnings figures
//   {lowestfield} / {highestfield}        the occupations those two belong to
//   {count}            "additional home visitors needed" — see COUNT_METRIC_ID
//   {category}         workforce shortage severity (lower-cased for prose)
//
// {count} and {category} are unused by the templates below, which name the
// under-3 and under-6 shortage counts directly. Both remain available.
//
// The registry's formatMetricValue owns the $ and % affixes, so templates must
// NOT write them — the CSV's "{female}%" would render "97.1%%".
//
// A blank line ("\n\n") in a template is a paragraph break. renderNarrative
// returns paragraphs of segments rather than a flat string so the panel can
// emphasize each substituted value ("text-primary fw-bolder") without the
// templates carrying markup; {place} and the occupation names read as prose
// and stay unemphasized.
//
// Wrapping prose in **double asterisks** bolds it — authored emphasis, separate
// from the automatic highlight on values, and renderable as <strong> without
// letting raw HTML into the templates. A bold run may contain tokens, so
// "**{total_HV} home visitors**" bolds the phrase and highlights the number.
//
// Bold is CATEGORY-SCOPED. A group paragraph describes every category in the
// group at once, so the sentence that answers the Metric dropdown differs by
// selection: "**education|About {ed_BA} ...**" bolds only while Education level
// is selected and reads as ordinary prose otherwise. The category id prefixes
// the run, ahead of a "|". Several ids may share a run, comma-separated, for a
// sentence that answers more than one selection.
//
// A run with no prefix is bold at every selection. Tagging keeps the copy in
// one place: the sentence is written once, and the prefix — not a duplicate
// paragraph per category — carries the emphasis.
//
// A suppression sentinel IS spliced into the sentence: "<5" is exactly what the
// map tooltip and the KPI chip already show for the same county, so the panel
// reports it verbatim rather than going blank while that number is visible an
// inch above it.
//
// A metric token whose value is missing or absent renders as "N/A" in place of
// the number, and the rest of the sentence still reads. It used to replace the
// WHOLE narrative with a shared fallback, and because the paragraph is a GROUP
// template that gate spanned every sentence in it — so a county whose
// home-visitor count is suppressed lost its "<5" to the capacity sentence
// beside it, whose shortage counts are blank in the source for exactly those
// counties (382 of 3,147 carry censored=1, and all 382 have no WF_Shortage_HV
// figure). See UNAVAILABLE_TEXT below.
//
// One case still takes the whole-paragraph fallback: every metric token in the
// template came back unavailable, so there is nothing to report about this
// selection at all.
//
// A derived range ({lowestCount} / {highestEstimate} and friends) that cannot be
// computed no longer does. Adjacent Fields — Number of graduates and Annual
// earnings — must never read "No data available" (client direction, 2026-09-16),
// so an underivable range renders BLANK_TEXT in each of its token slots and the
// sentence still renders around the gaps.
//
// That gate was also the reason one category could blank the other: the two
// share a single group template, so a county with no graduate counts lost its
// earnings sentence too. 2,060 of the 3,144 counties in the Year 2 source carry
// no Graduates_* value at all, while only 208 are missing every AnnualEarn_* —
// so roughly 1,930 counties were hiding earnings figures they actually had.
import { MISSING, getGroup, getCategory, getMetric, getCapacityShortageLabel } from './metricRegistry';
/**
 * The metric backing {count} — "N additional home visitors are needed to serve
 * 25% of low-income households with pregnant women or children under age 3".
 *
 * Backed by the real imported count as of 2026-08-19. It previously pointed at
 * UPL_HV_u3_cat, which made the token render the shortage LEVEL label ("Very High")
 * because Year 2 shipped no count field. No v2 template uses {count} — the
 * Workforce copy names WF_Shortage_HV_Needcnt_u3 and WF_Shortage_HV_Needcnt_u6
 * directly — but the token stays wired for copy that wants the shorthand.
 */
const COUNT_METRIC_ID = 'WF_Shortage_HV_Needcnt_u3';
/** The metric whose quartile code drives {category}'s shortage severity. */
const SHORTAGE_METRIC_ID = 'UPL_HV_u3_cat';
const WORKFORCE_PROFILE = '**home-visitors|In {place}, approximately {total_HV} home visitors provide home visiting ' +
    'services to families and children, supported by an estimated {Super} supervisors across ' +
    'local agencies, models, and funding sources.**' +
    '\n\n' +
    '**education|About {ed_BA} of home visitors have a bachelor’s degree** and ' +
    '**female|{female} are female.** ' +
    '**tenure|Home visitors have an average of {tenure} years of experience in home visiting** ' +
    'and are an **age|average of {age} years old.**';
//'\n\n' +
//'**capacity|To reach 25% of households with children under 3 or a pregnant woman, ' +
//'approximately {WF_Shortage_HV_Needcnt_u3} more home visitors are needed.** ' +
//'**capacity|To reach 25% of households ' +
//'with children under 6 or a pregnant woman, approximately {WF_Shortage_HV_Needcnt_u6} more home ' +
//'visitors are needed.**';
// County has no state-only demographics (education / tenure / age / female).
const WORKFORCE_PROFILE_COUNTY = 'In {place}, approximately {total_HV} home visitors provided home visiting ' +
    'services to families and children in 2025, supported by an estimated {Super} supervisors across ' +
    'local agencies, models, and funding sources.';
//'\n\n' +
//'**capacity|To reach 25% of households with children under 3 or a pregnant woman, ' +
//'approximately {WF_Shortage_HV_Needcnt_u3} more home visitors are needed.** ' +
//'**capacity|To reach 25% of households ' +
//'with children under 6 or a pregnant woman, approximately {WF_Shortage_HV_Needcnt_u6} more home ' +
//'visitors are needed.**';
// Two paragraphs: all households, then the same two counts restricted to below
// 200% of the federal poverty level. The break falls where the qualifier does,
// so the reader is not four near-identical sentences deep before noticing the
// population changed.
const HOUSEHOLDS = 'In {place}, there are {pregWomen_or_childu6} households with a child under 6 not yet in ' +
    'kindergarten or a pregnant woman. {pregWomen_or_childu3} households have a child under 3 or ' +
    'a pregnant woman.' +
    '\n\n' +
    'When restricting to U.S. households with incomes below 200% of the federal ' +
    'poverty level (FPL), there are an estimated {u200fpl_pregWomen_or_childu6} households with a child ' +
    'under 6 not yet in kindergarten or a pregnant woman. {u200fpl_pregWomen_or_childu3} ' +
    'households have a child under 3 or a pregnant woman.';
// One paragraph per category, which is also one per bold run — graduates and
// earnings answer different Metric selections and share no prose.
const GRADUATES_NONE = '**graduates|In {place}, there are no known students who completed credentials or degrees ' +
    'in undergraduate programs related to home visiting in the 2024 to 2025 academic year.**';
const EARNINGS_NONE = '**earnings|There are no known annual earnings for workers in adjacent fields.**';
const GRADUATES_LEAD = '**graduates|In {place} in 2025, the number of students who completed credentials or degrees ' +
    'in undergraduate programs related to home visiting ';
const GRADUATES_PARAGRAPHS = {
    allDifferent: GRADUATES_LEAD +
        'ranged from {lowestCount} in {lowestprogram} to {highestCount} in {highestprogram}.**',
    tiedHigh: GRADUATES_LEAD +
        'ranged from {lowestCount} in {lowestprogram} to {highestCount} each in {highestprogram}.**',
    tiedLow: GRADUATES_LEAD +
        'ranged from {lowestCount} each in {lowestprogram} to {highestCount} in {highestprogram}.**',
    tiedBoth: GRADUATES_LEAD +
        'ranged from {lowestCount} each in {lowestprogram} to {highestCount} each in {highestprogram}.**',
    allSame: GRADUATES_LEAD + 'was the same at {lowestCount} each in {lowestprogram}.**',
    single: GRADUATES_LEAD +
        'was {lowestCount} in {lowestprogram}, the only program with reported completion data.**',
    none: GRADUATES_NONE
};
const EARNINGS_LEAD = '**earnings|The average annual earnings for workers in adjacent fields ';
const EARNINGS_RANGE = EARNINGS_LEAD +
    'range from about {lowestEstimate} for {lowestfield} to about {highestEstimate} for {highestfield}.**';
const EARNINGS_PARAGRAPHS = {
    allDifferent: EARNINGS_RANGE,
    tiedHigh: EARNINGS_RANGE,
    tiedLow: EARNINGS_RANGE,
    tiedBoth: EARNINGS_RANGE,
    allSame: EARNINGS_LEAD + 'are about {lowestEstimate} for {lowestfield}.**',
    single: EARNINGS_LEAD +
        'are about {lowestEstimate} for {lowestfield}, the only field with available earnings data.**',
    none: EARNINGS_NONE
};
const ADJACENT_FIELDS = resolve => GRADUATES_PARAGRAPHS[classifyAdjacentRange(resolve, GRADUATE_METRICS).shape] +
    '\n\n' +
    EARNINGS_PARAGRAPHS[classifyAdjacentRange(resolve, EARNINGS_FIELDS).shape];
/** Group-level copy: the default paragraph for every category in the group. */
const NARRATIVES = {
    workforce: {
        national: WORKFORCE_PROFILE,
        state: WORKFORCE_PROFILE,
        county: WORKFORCE_PROFILE_COUNTY
    },
    demographics: HOUSEHOLDS,
    adjacent: ADJACENT_FIELDS
};
/**
 * Category-level overrides, consulted before the group template above.
 *
 * Empty by design — see the header note: v3 authors one paragraph per group per
 * level, so no category departs from its group. Add an entry here only when the
 * sheet gives a category its own copy, and scope it as narrowly as the sheet
 * does: `{ county: '...' }` overrides county and leaves national and state on
 * the group template.
 */
const CATEGORY_NARRATIVES = {};
// Shared fallbacks. {groupLabel} is lower-cased for mid-sentence use.
// There is no `suppressed` entry: a suppression sentinel is rendered in the
// sentence now, so it never reaches a fallback.
const SENTINEL_NARRATIVE_DEFAULT = 'No Data Available';
const SENTINEL_NARRATIVES = {
    total_HV: 'There is no known count of home visitors in {place}.',
    Super: 'There is no known count of supervisors in {place}.'
};
const SENTINEL_NARRATIVES_BY_CATEGORY = {
    graduates: GRADUATES_NONE,
    earnings: EARNINGS_NONE,
};
/**
 * What a metric token renders as when its value is unavailable for the current
 * selection. Matches formatMetricValue's output for an absent value, so the
 * sentence and the KPI chip an inch above it read the same way.
 *
 * Rendered WITH the same blue value highlight as a real number. It occupies the
 * slot a value would have, and styling it as prose made it read as part of the
 * sentence — the reader had to parse the wording to notice the figure was gone.
 * Highlighting it keeps the eye's path down the paragraph's values intact and
 * makes the gap legible as a gap.
 */
const UNAVAILABLE_TEXT = 'Missing';
/**
 * What a derived-range token renders as when the range cannot be computed at
 * all — a blank, leaving the slot the number would have occupied empty.
 *
 * Adjacent Fields must never fall back to "No data available", so the sentence
 * renders with gaps instead of being replaced. Deliberately NOT UNAVAILABLE_TEXT:
 * "Missing" is a claim about one figure, and these tokens come in sets of four
 * (two counts and the two programs they name), so a sentence reading "from
 * Missing in Missing to Missing in Missing" says the word four times.
 *
 * Blank in the source never means zero — no Graduates_* cell in the Year 2 county
 * file carries an explicit 0 — so the slot is left empty rather than filled with
 * a number or a phrase that would assert one.
 *
 * The single knob for this: change it to '—' or 'N/A' here and every derived
 * range that cannot be computed picks it up.
 */
const BLANK_TEXT = 'N/A';
/** The four adjacent-field earnings metrics, with occupation names for prose. */
const EARNINGS_FIELDS = [
    { metricId: 'AnnualEarn_ECE', name: 'early childhood educators' },
    { metricId: 'AnnualEarn_HC', name: 'registered nurses' },
    { metricId: 'AnnualEarn_SW', name: 'social workers' },
    { metricId: 'AnnualEarn_CHW', name: 'community health workers' }
];
/**
 * The four adjacent-field graduate counts, with program names for prose. Names
 * follow the CSV's Metric/Button wording for the Number of graduates row, which
 * reads as a field of study rather than the registry's occupation labels.
 */
const GRADUATE_METRICS = [
    { metricId: 'Graduates_ECE', name: 'early childhood and elementary education' },
    { metricId: 'Graduates_HC', name: 'nursing' },
    { metricId: 'Graduates_SW', name: 'social work' },
    { metricId: 'Graduates_CHW', name: 'public health' }
];
// Tokens that read as prose inside the sentence, so the panel must not
// emphasize them the way it does numbers.
const PROSE_TOKENS = new Set([
    '{place}', '{lowestprogram}', '{highestprogram}', '{lowestfield}', '{highestfield}'
]);
/**
 * Authored emphasis inside a template: `**bolded prose**`, Markdown-style.
 *
 * Bold is for wording the client wants to stand out; it is independent of the
 * blue highlight the panel already gives every substituted value. A run may
 * carry a token, so `**{total_HV} home visitors**` bolds the whole phrase and
 * still highlights the number inside it.
 *
 * The capture group keeps the delimiters in split()'s output so a run can be
 * told apart from the prose around it. Bold cannot nest, and it cannot span a
 * paragraph break — toSegments runs per paragraph, so a "**" left open at a
 * break renders its asterisks as prose. Copy that wants a bold sentence on
 * either side of a break closes the run and reopens it with the same tag, as
 * WORKFORCE_PROFILE's two capacity paragraphs do.
 */
const BOLD_RUN = /(\*\*[^*]+\*\*)/;
/**
 * The category tag on a bold run: `education|`, or `education,age|` for a
 * sentence that answers either selection. Anchored and restricted to the
 * kebab-case shape of a CategoryId so prose containing a stray "|" cannot be
 * mistaken for a tag.
 */
const BOLD_TAG = /^([a-z][a-z-]*(?:,[a-z][a-z-]*)*)\|([\s\S]*)$/;
const TOKEN_RUN = /(\{[A-Za-z_0-9]+\})/;
/**
 * Whether a tagged run is bold for the current selection, and the prose with
 * its tag stripped. An untagged run is bold at every selection.
 *
 * An unknown id in a tag can only be a typo — it would silently never bold —
 * so it warns and leaves the run unbolded rather than failing the render.
 */
function readBoldTag(prose, activeCategoryId) {
    const tagged = prose.match(BOLD_TAG);
    if (!tagged) {
        return { text: prose, bold: true };
    }
    const ids = tagged[1].split(',');
    const text = tagged[2];
    for (const id of ids) {
        if (!getCategory(id)) {
            console.warn(`metricNarratives: unknown category "${id}" tagging a bold run`);
            return { text, bold: false };
        }
    }
    return { text, bold: ids.indexOf(activeCategoryId) !== -1 };
}
/**
 * Split one paragraph into segments: bold runs first, then token substitution
 * within each run, so the two kinds of emphasis compose. A token with no entry
 * in `replacements` stays literal in the prose, as it did when this returned a
 * flat string.
 */
function toSegments(paragraph, replacements, activeCategoryId) {
    return paragraph
        .split(BOLD_RUN)
        .filter(run => run !== '')
        .flatMap(run => {
        const marked = run.startsWith('**') && run.endsWith('**');
        const { text: prose, bold } = marked
            ? readBoldTag(run.slice(2, -2), activeCategoryId)
            : { text: run, bold: false };
        return prose
            .split(TOKEN_RUN)
            .filter(part => part !== '')
            .map(part => {
            const replacement = replacements[part];
            return replacement === undefined
                ? { text: part, highlight: false, bold }
                : {
                    text: replacement,
                    highlight: !PROSE_TOKENS.has(part),
                    bold
                };
        });
    });
}
/**
 * Which CSV Level row applies: the Geography dropdown IS the sheet's Level
 * column, so the narrative reads the dropdown's own value. NarrativeScope and
 * GeoOption are the same three levels, which is why this is a pass-through.
 *
 * This deliberately does NOT look at the SELECTION — neither selectedCounty nor
 * selectedState. Choosing "County" lands on a county choropleth with no single
 * county clicked yet, and the county copy is what belongs there: v3 omits the
 * education, female, tenure, and age sentences at county level because those
 * fields do not exist at county level at all (ed_BA and friends carry no
 * countyField in the registry, and the sheet marks those categories "N/A" for
 * county). Keying off selectedCounty made the panel quote a bachelor's-degree
 * figure while the user was in County mode.
 *
 * Choosing "State" behaves the same way as of 2026-08-26, with one exception:
 * from National it stays on the United States row until a state is picked, so
 * the Level is 'state' while formatPlace still reads "the U.S." and the values
 * are the national row's; from County it carries over a state already drilled
 * into, and the Level, the place, and the values then all name that state.
 * The first case is the same disagreement County has always had, and the reason
 * the two functions are kept separate — see formatPlace below.
 */
export function getNarrativeScope(dashboardState) {
    return dashboardState.geoOption;
}
/**
 * How the selected geography reads inside a sentence: "County, ST" once a
 * county is clicked (the county name arrives without its "County"/"Parish"
 * suffix, which the API strips, so the state disambiguates repeated county
 * names), the state name when a state is selected, "the U.S." otherwise.
 *
 * stateCode reaches the dashboard in mixed case — lowercase from a map click,
 * uppercase from the search box — so it is uppercased here rather than trusted.
 * It is also empty whenever getStateFromPoint cannot parse an hc-key, which is
 * why the full state name remains the fallback: ", " with nothing after it is
 * worse than the longer spelling.
 *
 * Derived from the SELECTION, independently of getNarrativeScope's Level. The
 * two answer different questions and must be free to disagree: at county level
 * before a county is clicked — and, since 2026-08-26, at state level before a
 * state is picked — the level is 'county'/'state' (so that template runs) while
 * the place is still "the U.S." (so the sentence names the geography whose
 * numbers resolveMetricValue actually fell back to).
 */
export function formatPlace(dashboardState) {
    const { selectedCounty, selectedState, stateCode } = dashboardState;
    if (selectedCounty === "District of Columbia" || selectedState === "District of Columbia") {
        return "the District of Columbia";
    }
    if (selectedCounty != null && selectedState) {
        const stateAbbreviation = stateCode ? stateCode.toUpperCase() : '';
        return stateAbbreviation
            ? `${selectedCounty}, ${stateAbbreviation}`
            : `${selectedCounty}, ${selectedState}`;
    }
    if (!selectedState || selectedState === 'United States') {
        return 'the U.S.';
    }
    return selectedState;
}
/**
 * Whether a value can be spliced into prose.
 *
 * A suppression sentinel CAN. "<5" is what the map tooltip and the KPI chip
 * already display for the same county, so reporting it verbatim in the sentence
 * discloses nothing new and keeps the paragraph — supervisor counts are
 * suppressed in 724 of the 1,030 counties that carry a home-visitor count, and
 * failing closed on them blanked the panel on most of the counties that have
 * data. MISSING and absent have no such reading and still fail closed.
 */
function isUnavailable(value) {
    return value == null || value.raw === null || value.raw === undefined
        || value.raw === MISSING;
}
/** "A", "A and B", "A, B, and C" — serial comma from three names on. */
function joinNames(names) {
    var _a;
    if (names.length <= 1)
        return (_a = names[0]) !== null && _a !== void 0 ? _a : '';
    if (names.length === 2)
        return `${names[0]} and ${names[1]}`;
    return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}
/**
 * Which sentence an adjacent-field range needs, and the names on each end.
 *
 * Values that are not numbers are dropped before anything else, so a program
 * with no reported figure neither names itself nor moves an endpoint. Tie groups
 * come back in the source array's order rather than sorted order, so a list
 * reads ECE, nursing, social work, public health however the values fell.
 */
function classifyAdjacentRange(resolve, fields) {
    const present = fields
        .map(f => ({ name: f.name, value: resolve(f.metricId) }))
        .filter(f => typeof f.value.raw === 'number');
    const blank = { formatted: BLANK_TEXT, names: [] };
    if (present.length === 0) {
        return { shape: 'none', low: blank, high: blank };
    }
    const sorted = [...present].sort((a, b) => a.value.raw - b.value.raw);
    const lowest = sorted[0];
    const highest = sorted[sorted.length - 1];
    const lowNames = present.filter(f => f.value.raw === lowest.value.raw).map(f => f.name);
    const highNames = present.filter(f => f.value.raw === highest.value.raw).map(f => f.name);
    const shape = present.length === 1 ? 'single'
        : lowest.value.raw === highest.value.raw ? 'allSame'
            : lowNames.length > 1 && highNames.length > 1 ? 'tiedBoth'
                : lowNames.length > 1 ? 'tiedLow'
                    : highNames.length > 1 ? 'tiedHigh'
                        : 'allDifferent';
    return {
        shape,
        low: { formatted: lowest.value.formatted, names: lowNames },
        high: { formatted: highest.value.formatted, names: highNames }
    };
}
const DERIVED_RANGES = [
    {
        tokens: ['{lowestCount}', '{highestCount}', '{lowestprogram}', '{highestprogram}'],
        categoryId: 'graduates',
        resolveTokens: resolve => {
            const range = classifyAdjacentRange(resolve, GRADUATE_METRICS);
            if (range.shape === 'none')
                return null;
            return {
                '{lowestCount}': range.low.formatted,
                '{highestCount}': range.high.formatted,
                '{lowestprogram}': joinNames(range.low.names),
                '{highestprogram}': joinNames(range.high.names)
            };
        }
    },
    {
        tokens: ['{lowestEstimate}', '{highestEstimate}', '{lowestfield}', '{highestfield}'],
        categoryId: 'earnings',
        resolveTokens: resolve => {
            const range = classifyAdjacentRange(resolve, EARNINGS_FIELDS);
            if (range.shape === 'none')
                return null;
            return {
                '{lowestEstimate}': range.low.formatted,
                '{highestEstimate}': range.high.formatted,
                '{lowestfield}': joinNames(range.low.names),
                '{highestfield}': joinNames(range.high.names)
            };
        }
    }
];
/** Resolve one lookup layer at a scope; undefined means "no copy here". */
function resolveScoped(entry, scope, resolve) {
    if (entry === undefined)
        return undefined;
    const picked = typeof entry === 'string' || typeof entry === 'function'
        ? entry
        : entry[scope];
    if (picked === undefined)
        return undefined;
    return typeof picked === 'function' ? picked(resolve) : picked;
}
/**
 * The copy for a category at a scope: its own override if the sheet gives it
 * one, otherwise its group's paragraph. A category override that covers only
 * some scopes falls through to the group for the rest.
 */
function templateFor(groupId, categoryId, scope, resolve) {
    const override = resolveScoped(categoryId ? CATEGORY_NARRATIVES[categoryId] : undefined, scope, resolve);
    if (override !== undefined)
        return override;
    return resolveScoped(groupId ? NARRATIVES[groupId] : undefined, scope, resolve);
}
function sentinelNarrativeFor(metricId, paragraphCategories, scope, resolve) {
    var _a;
    const selectedCategory = (_a = getMetric(metricId)) === null || _a === void 0 ? void 0 : _a.categoryId;
    if (metricId && selectedCategory && paragraphCategories.includes(selectedCategory)) {
        const byMetric = resolveScoped(SENTINEL_NARRATIVES[metricId], scope, resolve);
        if (byMetric !== undefined)
            return byMetric;
    }
    for (const paragraphCategory of paragraphCategories) {
        const byCategory = resolveScoped(SENTINEL_NARRATIVES_BY_CATEGORY[paragraphCategory], scope, resolve);
        if (byCategory !== undefined)
            return byCategory;
    }
    return undefined;
}
/**
 * Build the narrative for a category at a geographic scope as paragraphs of
 * segments, with substituted data values flagged for emphasis. Returns null
 * when neither the category nor its group has authored copy, so the caller
 * renders nothing.
 */
export function renderNarrative(args) {
    var _a, _b, _c, _d, _e;
    const { groupId, categoryId, metricId, scope, place, resolve } = args;
    const template = templateFor(groupId, categoryId, scope, resolve);
    if (!template) {
        return null;
    }
    const groupLabel = ((_b = (_a = getGroup(groupId)) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : '').toLowerCase();
    const metricLabel = (_d = (_c = getMetric(metricId)) === null || _c === void 0 ? void 0 : _c.label) !== null && _d !== void 0 ? _d : '';
    const fill = (copy) => toSegments(copy
        .split('{place}').join(place)
        .split('{groupLabel}').join(groupLabel)
        .split('{metricLabel}').join(metricLabel), {}, categoryId);
    // Resolve every token in the paragraph up front. Every token gets a value —
    // a real one, UNAVAILABLE_TEXT, or BLANK_TEXT — so the paragraph is only ever
    // swapped wholesale when nothing in it resolved at all.
    const analyse = (paragraph) => {
        var _a, _b, _c;
        const tokens = (_a = paragraph.match(/\{[A-Za-z_0-9]+\}/g)) !== null && _a !== void 0 ? _a : [];
        const replacements = { '{place}': place };
        // How many metric-backed tokens the paragraph asked for, and how many came
        // back with something real (a number OR a suppression sentinel). Together
        // they answer "is there anything to say about this selection at all".
        let metricTokens = 0;
        let resolvedValues = 0;
        let derivedRanges = 0;
        let derivedFailures = 0;
        const categories = [];
        const note = (id) => {
            if (id && !categories.includes(id))
                categories.push(id);
        };
        /** A metric token with no value for this selection reads UNAVAILABLE_TEXT in place. */
        const unavailable = (token) => {
            replacements[token] = UNAVAILABLE_TEXT;
        };
        for (const token of tokens) {
            if (token === '{place}' || replacements[token] !== undefined) {
                continue;
            }
            const name = token.slice(1, -1);
            const derived = DERIVED_RANGES.find(range => range.tokens.includes(token));
            if (derived) {
                derivedRanges++;
                note(derived.categoryId);
                const values = derived.resolveTokens(resolve);
                if (!values) {
                    derivedFailures++;
                    /**
                     * Fill every token of a derived range that could not be
                     * computed, so the sentence renders with empty slots instead
                     * of vanishing. Takes the whole token set at once because the
                     * four move together: a range names two programs (or
                     * occupations) as well as two numbers.
                     */
                    for (const rangeToken of derived.tokens) {
                        replacements[rangeToken] = BLANK_TEXT;
                    }
                    continue;
                }
                Object.assign(replacements, values);
                continue;
            }
            if (name === 'count') {
                metricTokens++;
                note((_b = getMetric(COUNT_METRIC_ID)) === null || _b === void 0 ? void 0 : _b.categoryId);
                const value = resolve(COUNT_METRIC_ID);
                if (isUnavailable(value)) {
                    unavailable(token);
                    continue;
                }
                replacements[token] = value.formatted;
                resolvedValues++;
                continue;
            }
            if (name === 'category') {
                metricTokens++;
                note((_c = getMetric(SHORTAGE_METRIC_ID)) === null || _c === void 0 ? void 0 : _c.categoryId);
                const value = resolve(SHORTAGE_METRIC_ID);
                const label = typeof value.raw === 'number' ? getCapacityShortageLabel(value.raw) : null;
                if (label === null) {
                    unavailable(token);
                    continue;
                }
                replacements[token] = label;
                resolvedValues++;
                continue;
            }
            // Anything else must be a metric id.
            const metric = getMetric(name);
            if (!metric) {
                console.warn(`metricNarratives: unknown token ${token} in the template for ` +
                    `"${groupId}" / "${categoryId}" (${scope})`);
                continue;
            }
            metricTokens++;
            note(metric.categoryId);
            const value = resolve(name);
            if (isUnavailable(value)) {
                unavailable(token);
                continue;
            }
            replacements[token] = value.formatted;
            resolvedValues++;
        }
        return {
            paragraph, replacements, categories,
            metricTokens, resolvedValues, derivedRanges, derivedFailures
        };
    };
    const analysed = template.split('\n\n').map(analyse);
    // Nothing resolved for this selection at all, so a paragraph of "N/A" would
    // say less than the fallback does. Guarded on metricTokens because a
    // template may legitimately carry only derived tokens — ADJACENT_FIELDS does,
    // and those take the per-paragraph path below instead.
    const metricTokenTotal = analysed.reduce((sum, p) => sum + p.metricTokens, 0);
    const resolvedTotal = analysed.reduce((sum, p) => sum + p.resolvedValues, 0);
    if (metricTokenTotal > 0 && resolvedTotal === 0) {
        const categories = analysed.flatMap(p => p.categories);
        return [fill((_e = sentinelNarrativeFor(metricId, categories, scope, resolve)) !== null && _e !== void 0 ? _e : SENTINEL_NARRATIVE_DEFAULT)];
    }
    // One paragraph resolved nothing while another had data. A paragraph speaks
    // for a single category (see ADJACENT_FIELDS), so it is replaced on its own
    // and only where copy is authored for its subject. With none it renders
    // UNAVAILABLE_TEXT or BLANK_TEXT in the slots as before, which is what holds
    // Annual earnings to client direction (2026-09-16) that Adjacent Fields must
    // never read "No data available".
    return analysed.map(p => {
        const nothingResolved = p.metricTokens > 0
            ? p.resolvedValues === 0
            : p.derivedRanges > 0 && p.derivedFailures === p.derivedRanges;
        if (nothingResolved) {
            const authored = sentinelNarrativeFor(metricId, p.categories, scope, resolve);
            if (authored !== undefined)
                return fill(authored);
        }
        return toSegments(p.paragraph, p.replacements, categoryId);
    });
}
